import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Loader from '../../../components/Loader';

import SITE_PATHS from '../../../config/sitePaths';
import Wordings from '../../../config/stringTable';
import { LanguageConsumer } from '../../../config/LanguageContext';
import { runFlinks } from '../../../services/integrationsService';
import institutions from '../../../utils/institutions';
import Institution from '../../../components/Institution';
import BankingLogin from '../../../components/BankingLogin';
import BankingMFA from '../../../components/BankingMFA';
import responseHandlerFactory from './flinks-response-handler-factory';
import '../Banking.scss';

class Banking extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      longLoading: false,
      hasAgreed: false,
      currentInstitution: null,
      message: '',
      challenges: null,
      sessionId: null,
      loginId: null,
    };
  }

  goToSocial = () => {
    this.props.history.push(SITE_PATHS.SOCIAL_CONNECTORS, {
      scoringRequest: this.props.location.state.scoringRequest,
    });
  };

  onClickOnInstitution = (institution) => () => {
    this.setState({ currentInstitution: institution });
  };

  handleResponse = (response, language) => {
    this.setState({ loading: false, longLoading: false });
    responseHandlerFactory({
      onLoginSuccess: () => {
        this.setState({
          sessionId: response.RequestId,
          loginId: response.Login && response.Login.Id,
        });
      },
      onChallenge: () => {
        const challenges = response.SecurityChallenges.reduce((accumulator, challenge) => (
          [
            ...accumulator,
            {
              prompt: challenge.Prompt,
              iterables: challenge.Iterables,
              type: challenge.Type,
              question: '',
              answer: [],
            },
          ]
        ), []);
        this.setState({
          challenges,
          sessionId: response.RequestId,
        });
      },
      onError: (errorMessage) => {
        this.setState((state) => ({
          message: Wordings[language][errorMessage],
          currentInstitution: null,
          challenges: state.challenges && state.challenges.map((challenge) => ({
            ...challenge,
            answer: [],
          })),
        }));
      },
      onUnrecognizedResponse: () => {
        this.setState({
          message: Wordings[language]['Unrecognized response'],
          currentInstitution: null,
        });
      },
    })(response)();
  };

  runFlinksIntegration = async (data, language) => {
    const { scoringRequest: { requestId, clientId } } = this.props.location.state;
    this.setState({ loading: true });
    runFlinks(
      requestId,
      clientId,
      data,
    )
      .then((response) => {
        this.handleResponse(response[0], language);
      })
      .catch((e) => {
        this.setState({
          loading: false,
          longLoading: false,
          message: e.message,
        });
        console.error(e); // eslint-disable-line no-console
      });
  };

  onLoginSubmit = async (cred, language) => {
    const { code: institution } = this.state.currentInstitution;
    const { username, password } = cred;
    return this.runFlinksIntegration({
      login: {
        institution,
        username,
        password,
      },
    }, language);
  };

  onMFASubmit = async (language) => {
    const { challenges, sessionId } = this.state;
    const securityResponses = challenges.reduce(
      (accumulator, challenge) => ({ ...accumulator, [challenge.question]: challenge.answer }),
      {},
    );
    return this.runFlinksIntegration({
      mfa: {
        sessionId,
        securityResponses,
      },
    }, language);
  };

  updateChallenges = ({ answer, question, prompt }) => {
    this.setState((state) => {
      const challenges = state.challenges.map((challenge) => {
        if (challenge.prompt === prompt) {
          return {
            ...challenge,
            question: question || challenge.question,
            answer: Array.isArray(answer) ? answer : [answer],
          };
        }
        return challenge;
      });
      return { challenges, message: '' };
    });
  };

  renderMFA(language) {
    return (
      <form
        className="banking-form"
        onSubmit={(e) => {
          e.preventDefault();
          this.onMFASubmit(language);
        }}
        noValidate
      >
        {this.state.challenges.map((challenge) => (
          <BankingMFA
            key={challenge.prompt}
            challenge={challenge}
            handleChange={this.updateChallenges}
          />
        ))}
        <button type="submit">{Wordings[language].Submit}</button>
      </form>
    );
  }

  renderLogin(language) {
    return (
      <BankingLogin
        onSubmit={(credentials) => this.onLoginSubmit(credentials, language)}
        currentInstitution={this.state.currentInstitution}
      />
    );
  }

  renderInstitution(language) {
    return (
      <>
        {institutions.map((institution) => (
          <Institution
            key={institution.code}
            institution={institution}
            onClickOnInstitution={this.onClickOnInstitution}
          />
        ))}
        <div className="skip-button">

          <button
            type="button"
            className="btn btn-success"
            onClick={this.goToSocial}
          >
            {Wordings[language]['Skip banking data']}
          </button>
        </div>
      </>
    );
  }

  renderTerms() {
    return (
      <LanguageConsumer>
        {({ language }) => (
          <div className="banking-form agreement-container">
            <h6>{Wordings[language].TSWants}</h6>
            <ul className="list-unstyled">
              <li>
                <FontAwesomeIcon icon="check-circle" size="xs" color="gray" />
                {` ${Wordings[language].AccessTransactionHistory}`}
              </li>
              <li>
                <FontAwesomeIcon icon="check-circle" size="xs" color="gray" />
                {` ${Wordings[language].AccessAccountNumber}`}
              </li>
              <li>
                <FontAwesomeIcon icon="check-circle" size="xs" color="gray" />
                {` ${Wordings[language].AccessProfile}`}
              </li>
            </ul>
            <h5 className="header-5">
              {` ${Wordings[language].AgreeToConfirm}`}
            </h5>
            <div className="buttons">
              <button
                className="btn"
                type="button"
                onClick={() => this.setState({ currentInstitution: null })}
              >
                {Wordings[language].Cancel}
              </button>
              <button type="button" onClick={() => this.setState({ hasAgreed: true })}>
                {Wordings[language].AgreeAndContinue}
              </button>
            </div>
          </div>
        )}
      </LanguageConsumer>
    );
  }

  renderError(msg) {
    return (
      <LanguageConsumer>
        {({ language }) => (
          <div className="banking-container container">
            <div className="error-container">
              <div>
                <FontAwesomeIcon icon="times-circle" size="3x" className="error-icon" />
              </div>
              <br />
              <h5>
                {Wordings[language]['Error Occurred']}
              </h5>
              <br />
              <p>
                {msg}
              </p>
              <button
                type="button"
                onClick={() => this.setState({ message: '' })}
              >
                {Wordings[language].Retry.toUpperCase()}
              </button>
            </div>
          </div>
        )}
      </LanguageConsumer>
    );
  }

  renderWorkFlow(language) {
    return (
      <>
        <div className="institution-box">
          <img
            className="institution-icon"
            src={`/icons/institutions/${this.state.currentInstitution.code}.svg`}
            alt={this.state.currentInstitution.name}
          />
          <span>{this.state.currentInstitution.name}</span>
        </div>
        {this.state.challenges && this.renderMFA(language)}
        {this.state.currentInstitution
          && this.state.hasAgreed
          && !this.state.challenges
          && this.renderLogin(language)}
        {this.state.currentInstitution && !this.state.hasAgreed && this.renderTerms()}
      </>
    );
  }

  renderThankYou() {
    return (
      <LanguageConsumer>
        {({ language }) => (
          <div className="banking-container container">
            <div className="thank-you-container">
              <h5 className="thank-you-title">{Wordings[language]['Thank you']}</h5>
              <h5>{Wordings[language]['Your account was successfully connected!']}</h5>
              <div>
                <FontAwesomeIcon icon="check-circle" size="4x" className="thanks-icon" />
              </div>
            </div>
            <div className="skip-button">
              <button
                type="button"
                className="btn btn-success"
                onClick={this.goToSocial}
              >
                {Wordings[language].Continue}
              </button>
            </div>
          </div>
        )}
      </LanguageConsumer>
    );
  }

  renderLoader() {
    const { name } = this.state.currentInstitution;
    setTimeout(() => this.state.loading && this.setState({ longLoading: true }), 3000);

    return (
      <LanguageConsumer>
        {({ language }) => (
          <>
            <Loader />
            <h6>
              {`${Wordings[language].AuthorizingWith} ${name}`}
            </h6>
            <p className="loading-text">
              {this.state.longLoading
                ? Wordings[language].BanksCanTakeAwhile
                : Wordings[language].SendingCredentials}
            </p>
          </>
        )}
      </LanguageConsumer>

    );
  }

  render() {
    if (this.state.loading) return this.renderLoader();
    if (this.state.message) return this.renderError(this.state.message);
    if (this.state.loginId) return this.renderThankYou();
    return (
      <LanguageConsumer>
        {({ language }) => (

          <div className="banking-container container">
            {this.state.currentInstitution
              ? this.renderWorkFlow(language)
              : this.renderInstitution(language)}
          </div>
        )}
      </LanguageConsumer>
    );
  }
}

export default Banking;
